import { LitElement, css, html } from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'

class NovaQuantityInput extends LitElement {
  static properties = {
    key: {},
    label: {},
    initial: {
      type: Number,
    },
    value: {
      state: true,
    },
    max: {
      type: String,
      attribute: 'max',
    },
    type: {
      type: String,
      attribute: 'type',
    },
  }

  constructor() {
    super()
    this.broadcast = this.broadcast.bind(this)
    this.id = Math.random() * 100000
    this.max = ''
    this.type = ''
  }

  connectedCallback() {
    super.connectedCallback()
    this.value = this.initial || 1
    this.updateContent = this.updateContent.bind(this)
    document.addEventListener(
      'nova:product:render-quantity-input',
      this.updateContent,
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener(
      'nova:product:render-quantity-input',
      this.updateContent,
    )
  }

  updateContent(event) {
    const newMax = event.detail.html?.getAttribute('max')
    if (!newMax) return
    this.setAttribute('max', newMax)
    this.max = newMax
    if (this.value > Number(newMax)) {
      this.value = Number(newMax) < 1 ? 1 : Number(newMax)
      this.broadcast()
    }
  }

  willUpdate(changedProperties) {
    if (changedProperties.has('initial')) {
      this.value = this.initial
    }
  }

  broadcast() {
    const $input = this.renderRoot.querySelector('slot[name="input"]')

    if ($input) {
      $input.value = this.value
    }

    this.dispatchEvent(
      new CustomEvent('changeQuantityStarted', {
        bubbles: true,
        composed: true,
        detail: {
          key: this.key,
          value: this.value,
        },
      }),
    )
  }

  increment() {
    if (this.max !== '' && this.value >= Number(this.max)) return

    this.value += 1
    this.broadcast()
  }

  decrement() {
    if (!this.value) return
    if (this.type === 'product' && this.value === 1) return

    this.value -= 1
    this.broadcast()
  }

  override(e) {
    let value = Math.max(0, e.target.value)
    if (this.max !== '' && value > Number(this.max)) {
      value = this.max
    }
    this.value = value
    this.broadcast()
  }

  render() {
    return html`
      <div class="wrapper">
        <button @click=${this.decrement} name="decrease" class="decrease-button" ?disabled=${this.type === 'product' && this.value === 1}>
          ${paths.minusSmall}
        </button>
        <label class="sr-only" for="Quantity-${this.id}">
          ${this.label | 'Quantity'}
        </label>
        <slot name="input"></slot>
        <input
          id="Quantity-${this.id}"
          class="input"
          type="number"
          name="updates[]"
          .value=${this.value}
          @change=${this.override}
          min="0"
          max=${this.max}
        />
        <button @click=${this.increment} name="increase" class="increase-button" ?disabled=${this.value === Number(this.max)}>
          ${paths.plusSmall}
        </button>
      </div>
    `
  }

  static styles = [
    styles.a11y,
    styles.variables,
    css`
      .wrapper {
        border: 1px solid var(--nova-brand-neutral);
        border-radius: var(--nova-border-radius-l);
        box-sizing: border-box;
        color: #000;
        position: relative;
        display: flex;
        height: 55px;
        width: 107px;
      }
      .input {
        color: var(--nova-brand-ink);
        background-color: transparent;
        font: var(--nova-p3-font);
        line-height: 130%;
        text-align: center;
        border: 0;
        width: 100%;
        flex-grow: 1;
        -webkit-appearance: none;
        appearance: none;
        -moz-appearance: textfield;
      }
      button {
        height: 55px;
        width: calc(var(--nova-space-sm) + var(--nova-space-xs));
        flex-shrink: 0;
        border: 0;
        background-color: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--nova-brand-ink);
        padding: 0;
      }
      button[disabled] {
        color: var(--nova-brand-neutral);
      }
      .decrease-button {
        padding-left: var(--nova-space-sm);
      }
      .increase-button {
        padding-right: var(--nova-space-sm);
      }
      .input:-webkit-autofill,
      .input:-webkit-autofill:hover,
      .input:-webkit-autofill:active {
        box-shadow: 0 0 0 10rem var(--color-background) inset !important;
        -webkit-box-shadow: 0 0 0 10rem var(--color-background) inset !important;
      }
      .input::-webkit-outer-spin-button,
      .input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      .input:focus {
        outline: none;
      }
      .input:focus-visible,
      button:focus-visible {
        outline: 2px solid var(--nova-utility-focus);
      }
    `,
  ]
}

customElements.define('nova-quantity-input', NovaQuantityInput)
